<script setup lang="ts">
import { computed, useAttrs, useSlots, type PropType } from 'vue'
import OenUiRipple from '../OenUiRipple/OenUiRipple.vue'
import OenUiSpinner from '../OenUiSpinner/OenUiSpinner.vue'
import { sharedButtonProps } from './props'

const props = defineProps({
  ...sharedButtonProps,
  buttonType: {
    type: String as PropType<'button' | 'a'>,
    default: 'button',
    validator(value: string) {
      return value === 'button' || value === 'a'
    },
  },
})

const themeClass = computed(() => {
  return `oen-ui-button--${props.theme}`
})

const loadingClass = computed(() => {
  return props.loading ? 'oen-ui-button--loading' : null
})

const isDisabled = computed(() => {
  return !!(attrs.disabled || attrs.loading || props.loading) && props.buttonType === 'button'
})

const attrs = useAttrs()
const slots = useSlots()
</script>

<template>
  <Component
    :is="buttonType"
    class="oen-ui-button"
    :class="[themeClass, loadingClass]"
    v-bind="props.buttonType === 'button' ? { ...attrs, disabled: isDisabled } : attrs"
    :analytics-name="analyticsName"
  >
    <OenUiRipple
      v-if="theme === 'icon'"
      class="oen-ui-button-inner"
      centered
      :disabled="isDisabled"
    >
      <slot />
    </OenUiRipple>
    <OenUiRipple v-else class="oen-ui-button-inner" :disabled="isDisabled">
      <span v-if="slots.leftIcon" class="oen-ui-button-left-icon block">
        <!-- @slot leftIcon: For the left Icon -->
        <slot name="leftIcon" />
      </span>
      <slot />
      <span v-if="slots.rightIcon" class="oen-ui-button-right-icon block">
        <!-- @slot rightIcon: For the right Icon -->
        <slot name="rightIcon" />
      </span>
      <OenUiSpinner
        :appear="true"
        :size="1.25"
        :loading="loading"
        class="oen-ui-button-loading-icon"
      ></OenUiSpinner>
    </OenUiRipple>
  </Component>
</template>

<style scoped>
.oen-ui-button {
  @apply rounded-xxl;
  @apply outline-none box-border;

  :deep(.md-ripple-enter-from) {
    @apply opacity-100;
  }

  /* Modifiers */
  &.w-full {
    .oen-ui-button-left-icon {
      @apply ms-0;
    }

    .oen-ui-button-right-icon {
      @apply me-0;
    }
  }

  &[disabled] {
    @apply cursor-not-allowed;
  }

  &.oen-ui-button--loading {
    @apply cursor-wait;
  }

  &.oen-ui-button--primary {
    @apply text-white bg-oen-ui-primary;

    .oen-ui-button-inner {
      @apply px-8;
    }

    &:hover:not(:disabled) {
      @apply bg-oen-ui-primary-button-hover;
    }

    &:focus {
      @apply bg-oen-ui-primary-button-hover;
    }

    &:disabled:not(.oen-ui-button--loading) {
      @apply text-tertiary-text bg-gray-light;
    }

    :deep(.md-ripple-wave) {
      @apply bg-white-dim;
    }
  }

  &.oen-ui-button--secondary {
    @apply border;
    @apply text-oen-ui-primary border-oen-ui-primary-light;

    .oen-ui-button-inner {
      @apply px-6;
    }

    &:hover:not(:disabled) {
      @apply bg-oen-ui-primary-lighter;
    }

    &:focus {
      @apply bg-oen-ui-primary-lighter;
    }

    &:disabled:not(.oen-ui-button--loading) {
      @apply text-tertiary-text border-gray-light;
    }

    :deep(.md-ripple-wave) {
      @apply bg-oen-ui-primary-lighter;
    }
  }

  &.oen-ui-button--text {
    @apply text-oen-ui-primary min-h-10;

    .oen-ui-button-inner {
      @apply py-2;
    }

    &:hover:not(:disabled) {
      @apply bg-oen-ui-primary-lighter;
    }

    &:focus {
      @apply bg-oen-ui-primary-lighter;
    }

    &:disabled:not(.oen-ui-button--loading) {
      @apply text-tertiary-text;
    }

    :deep(.md-ripple-wave) {
      @apply bg-oen-ui-primary-lighter;
    }
  }

  &.oen-ui-button--text-secondary {
    @apply text-secondary-text min-h-10;

    .oen-ui-button-inner {
      @apply py-2;
    }

    &:hover:not(:disabled) {
      @apply bg-gray-lighter;
    }

    &:focus {
      @apply bg-gray-lighter;
    }

    &:disabled:not(.oen-ui-button--loading) {
      @apply text-tertiary-text;
    }

    :deep(.md-ripple-wave) {
      @apply bg-gray-lighter;
    }
  }

  &.oen-ui-button--icon {
    @apply text-secondary-text;

    .oen-ui-button-inner {
      @apply p-0;
      @apply w-12 h-12;
    }

    svg {
      @apply w-6 h-6;
    }

    &:hover:not(:disabled) {
      @apply bg-gray-lightest text-primary-text;
    }

    &:active:not(:disabled) {
      @apply text-primary-text;
    }

    &:focus {
      @apply bg-gray-lighter text-primary-text;
    }

    &:disabled:not(.oen-ui-button--loading) {
      @apply text-tertiary-text;
    }

    :deep(.md-ripple-wave) {
      @apply bg-gray-lighter;
    }
  }
}

.oen-ui-button-inner {
  @apply inline-flex items-center justify-center;
  @apply type-button;
  @apply px-4 py-3;
  @apply rounded-xxl;
  @apply w-full;
  @apply transition ease-in-out duration-180;

  &:disabled {
    @apply text-tertiary-text;
  }

  &:focus {
    @apply shadow-oen-ui-1;
  }

  /* Elements */
  .oen-ui-button-left-icon,
  .oen-ui-button-right-icon {
    @apply flex items-center;

    :deep(svg) {
      @apply w-4_5 h-4_5;
    }
  }

  .oen-ui-button-left-icon {
    @apply me-2;
    @apply -ms-1_5;
  }

  .oen-ui-button-right-icon {
    @apply ms-2;
    @apply -me-1_5;
  }

  .oen-ui-button-loading-icon {
    @apply w-5 relative start-2.5 shrink-0;
  }

  :deep(.md-ripple-enter-active) {
    @apply duration-630;

    &.md-centered {
      @apply duration-630;
    }
  }
}
</style>
